import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { deleteTransaction, showUserFreePlayTransactions } from "../../../../services/admin/Transaction.tsx";
import { collection, doc, runTransaction, serverTimestamp, Timestamp } from 'firebase/firestore';
import db from "../../../../services/Firebase.js";
import { Users, Transaction } from "../../../../models/User.tsx";
import { showBetsFreePlays } from "../../../../services/admin/BetsService.tsx";
import { BetDetails } from "../../../../models/MyBetModel.tsx";
import { logUserActivity } from "../../../../services/player/LoginService.tsx";
import { useUser } from '../../../../context/UserContext.tsx';
import { formatNumbers } from "../../../../helpers/functions.tsx";
import { formatAdminFiguresDateTime, formatAdminFiguresShortDate } from "../../../../helpers/adminFiguresTime.tsx";
import { useOutletContext } from 'react-router-dom';
import { VisitedUserOutletContext } from '../../../../pages/admin/LayoutUser.tsx';
import { useAgentPermissions } from '../../../../helpers/useAgentPermissions.tsx';

const FREE_PLAY_SKELETON_ROWS = 8;

type FreePlayLedgerEntry = {
    id: string;
    kind: 'transaction' | 'bet';
    customer: string;
    date: Date;
    description: string;
    credit: number;
    debit: number;
    balance: number;
};

const toEntryDate = (value: Timestamp | Date | undefined): Date => {
    if (!value) return new Date(0);
    if (value instanceof Date) return value;
    if (typeof value.toDate === 'function') return value.toDate();
    return new Date(0);
};

const freePlayTxAmounts = (tx: Transaction): { credit: number; debit: number } => {
    const amount = tx.amount ?? 0;
    const type = String(tx.transactionType ?? "").toLowerCase();
    if (type === "deposit" || type === "refund") return { credit: amount, debit: 0 };
    if (type === "withdrawal") return { credit: 0, debit: amount };
    return { credit: 0, debit: 0 };
};

const buildFreePlayLedger = (
    transactions: Transaction[],
    bets: BetDetails[],
): FreePlayLedgerEntry[] => {
    const entries: Omit<FreePlayLedgerEntry, 'balance'>[] = [];

    transactions.forEach(tx => {
        if (!tx.id) return;
        const { credit, debit } = freePlayTxAmounts(tx);
        if (credit <= 0 && debit <= 0) return;

        entries.push({
            id: tx.id,
            kind: 'transaction',
            customer: tx.customer,
            date: toEntryDate(tx.createDate),
            description: tx.description,
            credit,
            debit,
        });
    });

    bets.forEach(bet => {
        const betAmount = bet.bet ?? 0;
        if (betAmount <= 0) return;

        entries.push({
            id: bet.ticket || `bet-${bet.betDate?.seconds ?? entries.length}`,
            kind: 'bet',
            customer: bet.player?.userName || '',
            date: toEntryDate(bet.betDate),
            description: bet.description || bet.betName || 'Free Play Wager',
            credit: 0,
            debit: betAmount,
        });
    });

    entries.sort((a, b) => a.date.getTime() - b.date.getTime());

    let balance = 0;
    const withBalance = entries.map(entry => {
        balance += entry.credit - entry.debit;
        return { ...entry, balance };
    });

    return withBalance.reverse();
};

const filterLedgerByDays = (entries: FreePlayLedgerEntry[], filter: string): FreePlayLedgerEntry[] => {
    if (filter === 'all') return entries;

    const days = Number(filter);
    if (!Number.isFinite(days)) return entries;

    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    return entries.filter(entry => entry.date.getTime() >= cutoff);
};

const formatLedgerDate = (date: Date) =>
    formatAdminFiguresDateTime(date, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });

const FreePlayTableSkeleton = () => (
    <>
        {Array.from({ length: FREE_PLAY_SKELETON_ROWS }).map((_, index) => (
            <tr key={index} className="user-transaction-skeleton-row" aria-hidden="true">
                <td><span className="rect-placeholder placeholder-transaction-id" /></td>
                <td><span className="rect-placeholder placeholder-transaction-date" /></td>
                <td><span className="rect-placeholder placeholder-transaction-description" /></td>
                <td><span className="rect-placeholder placeholder-transaction-amount" /></td>
                <td><span className="rect-placeholder placeholder-transaction-amount" /></td>
            </tr>
        ))}
    </>
);

const UserFreePlay = () => {
    const { user } = useParams<{ user: string }>();
    const { user: currentUser, userIpData: userIpDataAdmin } = useUser();
    const { visitedUser } = useOutletContext<VisitedUserOutletContext>();
    const [formData, setFormData] = useState<Users | null>(null);
    const [dataTransaction, setDataTransaction] = useState<Transaction[] | null>(null);
    const [filter, setFilter] = useState("7");
    const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
    const [formTransaction, setFormTransaction] = useState({
        createDate: formatAdminFiguresShortDate(),
        agent: '',
        customer: '',
        transactionType: 'deposit',
        description: 'Customer Deposit **Free Play **',
        amount: '',
        createBy: '',
        transactionFree: true,
    })
    const [bets, setbets] = useState<BetDetails[]>([]);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    const { allowFreePlay } = useAgentPermissions(currentUser);
    const canGiveFreePlay = currentUser?.userType !== 'subAgent' || allowFreePlay;

    const clearFormTransaction = () => {
        setFormTransaction({
            createDate: formatAdminFiguresShortDate(),
            agent: '',
            customer: '',
            transactionType: 'deposit',
            description: 'Customer Deposit **Free Play **',
            amount: '',
            createBy: '',
            transactionFree: true,
        });
    };

    const resetModalState = useCallback(() => {
        setIsLoading(false);
        setIsSuccess(false);
        setErrorMessage(null);
        clearFormTransaction();
    }, []);

    useEffect(() => {
        const modalEl = modalRef.current;
        if (!modalEl) return;

        const handleHidden = () => resetModalState();

        modalEl.addEventListener('hidden.bs.modal', handleHidden);
        return () => modalEl.removeEventListener('hidden.bs.modal', handleHidden);
    }, [resetModalState]);

    const loadFreePlayData = useCallback(async (showPageSkeleton = true) => {
        if (!user) return;
        if (showPageSkeleton) setIsPageLoading(true);
        try {
            const [transactions, pendingBets] = await Promise.all([
                showUserFreePlayTransactions(user),
                showBetsFreePlays(user),
            ]);
            setDataTransaction(transactions);
            setbets(pendingBets);
        } catch (error) {
            console.error('Error loading free play data:', error);
        } finally {
            if (showPageSkeleton) setIsPageLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (visitedUser) setFormData(visitedUser);
    }, [visitedUser]);

    useEffect(() => {
        loadFreePlayData();
    }, [loadFreePlayData]);

    const totalFreePlay = useMemo(
        () => bets
            .filter(bet => bet.betStatus === 'pending')
            .reduce((sum, bet) => sum + (bet.bet || 0), 0),
        [bets]
    );

    const ledgerEntries = useMemo(
        () => buildFreePlayLedger(dataTransaction ?? [], bets),
        [dataTransaction, bets]
    );

    const filteredLedgerEntries = useMemo(
        () => filterLedgerByDays(ledgerEntries, filter),
        [ledgerEntries, filter]
    );


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        const sanitizedValue = name === 'amount' ? value.replace(/[^0-9.]/g, '') : value;

        setFormTransaction(prevData => {
            const updatedData = {
                ...prevData,
                [name]: sanitizedValue,
            };
            if (name === 'transactionType') {
                updatedData.description = value === 'deposit'
                    ? 'Customer Deposit **Free Play **'
                    : 'Customer Withdrawal **Free Play **';
            }
            return updatedData;
        });
    };

    const refreshFreePlayData = () => loadFreePlayData(false);

    const saveTransaction = async () => {
        if (!formData?.id || !currentUser?.userName) {
            setErrorMessage('User profile not loaded. Please refresh the page.');
            return;
        }

        if (!canGiveFreePlay) {
            setErrorMessage('You are not allowed to give Free Play.');
            return;
        }

        const amount = parseFloat(formTransaction.amount);
        if (!formTransaction.amount.trim() || Number.isNaN(amount) || amount <= 0) {
            setErrorMessage('Please enter a valid amount.');
            return;
        }

        const currentFreePlay = formData.freePlay || 0;
        if (formTransaction.transactionType === 'withdrawal' && amount > currentFreePlay) {
            setErrorMessage('Insufficient free play balance.');
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);
        setIsSuccess(false);

        try {
            const newFreePlay = await runTransaction(db, async (transaction) => {
                const userRef = doc(db, 'users', formData.id!);
                const transactionRef = doc(collection(db, 'transaction'));
                const userDoc = await transaction.get(userRef);

                if (!userDoc.exists()) {
                    throw new Error('User not found');
                }

                const previousFreePlay = Number(userDoc.data().freePlay) || 0;
                let updatedFreePlay = previousFreePlay;

                if (formTransaction.transactionType === 'deposit') {
                    updatedFreePlay = previousFreePlay + amount;
                } else if (formTransaction.transactionType === 'withdrawal') {
                    if (amount > previousFreePlay) {
                        throw new Error('Insufficient free play balance.');
                    }
                    updatedFreePlay = previousFreePlay - amount;
                }

                transaction.update(userRef, {
                    freePlay: updatedFreePlay,
                    lastUpdated: serverTimestamp(),
                });

                transaction.set(transactionRef, {
                    createDate: Timestamp.fromDate(new Date()),
                    agent: formData.parent,
                    customer: formData.userName,
                    transactionType: formTransaction.transactionType,
                    description: formTransaction.description,
                    amount,
                    createBy: currentUser.userName,
                    userType: formData.userType,
                    transactionFree: true,
                });

                return updatedFreePlay;
            });

            setFormData(prev => prev ? { ...prev, freePlay: newFreePlay } : null);
            setIsSuccess(true);
            clearFormTransaction();

            logUserActivity(
                currentUser.userName,
                `User Free Play: ${formData.userName} amount: ${amount}`,
                userIpDataAdmin
            );

            try {
                await refreshFreePlayData();
            } catch (error) {
                console.error('Error refreshing free play data:', error);
            }
        } catch (error) {
            console.error(error);
            const message = error instanceof Error ? error.message : 'Error saving transaction.';
            setErrorMessage(
                message === 'User not found' || message === 'Insufficient free play balance.'
                    ? message
                    : 'Error saving transaction.'
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterChange = (e: any) => {
        const selectedFilter = e.target.value;
        setFilter(selectedFilter);
    };

    const handleDeleteTransaction = async () => {
        if (!selectedTransactionId || !currentUser?.userName) {
            alert('Please select a transaction to delete.');
            return;
        }
        try {
            await deleteTransaction(selectedTransactionId, currentUser?.userName);
            logUserActivity(currentUser!.userName, `User Delete Free Play: ${formData?.userName}`, userIpDataAdmin);

            alert('Transaction successfully deleted.');
            setSelectedTransactionId(null);
            await refreshFreePlayData();

        } catch (error) {
            console.error(error);
        }
    };

    const handleRowSelection = (entry: FreePlayLedgerEntry) => {
        if (entry.kind !== 'transaction') return;
        setSelectedTransactionId(entry.id);
    };


    return (
        <>
            <div className='container-transaction mt-1'>
                <div className='container-option mt-2'>
                    <div className='transaction'>
                        <span className='letters-selected color-display-freeplay'>Display</span>
                        <div className="select-wrapper-admin">
                            <select className='select-box-admin' onChange={handleFilterChange}>
                                <option value="7">Last 7 days</option>
                                <option value="30">Last 30 days</option>
                                <option value="90">Last 90 days</option>
                                <option value="all">All</option>
                            </select>
                            <span className="select-arrow-admin" aria-hidden="true">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                        </div>
                        <div className="line-block2"></div>
                    </div>
                    <div className='transaction'>
                        <span className='letters-selected'>Balance</span>
                        {isPageLoading ? (
                            <span className="rect-placeholder placeholder-freeplay-balance" aria-hidden="true" />
                        ) : (
                            <input className="input-basic" value={formatNumbers(formData?.freePlay ?? 0)} readOnly />
                        )}
                    </div>
                    <div className='transaction'>
                        <span className='letters-selected'>Pending</span>
                        {isPageLoading ? (
                            <span className="rect-placeholder placeholder-freeplay-balance" aria-hidden="true" />
                        ) : (
                            <input className="input-basic" value={formatNumbers(totalFreePlay)} readOnly />
                        )}
                    </div>
                </div>
                <div className='table-responsive freeplay'>
                    <table className="mt-3 table table-freeplay">
                        <thead className="header-customer">
                            <tr>
                                <th className="col-customer"><span>Customer</span></th>
                                <th className="col-date"><span>Date</span></th>
                                <th className="col-description"><span>Description</span></th>
                                <th className="col-credit"><span>Credit</span></th>
                                <th className="col-debit"><span>Debit</span></th>
                            </tr>
                        </thead>
                        <tbody className="body-customer" aria-busy={isPageLoading}>
                            {isPageLoading ? (
                                <FreePlayTableSkeleton />
                            ) : (
                                <>
                            {filteredLedgerEntries.length > 0 ? filteredLedgerEntries.map((entry, index) => {
                                const isSelected = entry.kind === 'transaction' && selectedTransactionId === entry.id;
                                const isBet = entry.kind === 'bet';
                                const rowBg = index % 2 === 0 ? '#e6e6e6' : '#ffffff';

                                return (
                                    <tr
                                        key={`${entry.kind}-${entry.id}`}
                                        onClick={() => handleRowSelection(entry)}
                                        style={{
                                            cursor: isBet ? 'default' : 'pointer',
                                            backgroundColor: isSelected ? '#ebf584' : rowBg,
                                        }}
                                    >
                                        <td>{entry.customer}</td>
                                        <td>{formatLedgerDate(entry.date)}</td>
                                        <td>{entry.description}</td>
                                        <td>{entry.credit > 0 ? formatNumbers(entry.credit) : ''}</td>
                                        <td>{entry.debit > 0 ? formatNumbers(entry.debit) : ''}</td>
                                    </tr>
                                );
                            }) : (
                                <tr>
                                    <td colSpan={5} className="text-center">No data</td>
                                </tr>
                            )}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
                <button className='mt-2 transaction-delete' onClick={handleDeleteTransaction} disabled={isPageLoading}>Delete</button>
            </div >
            {currentUser?.status !== "Read-Only" && currentUser?.status !== "Disabled" && canGiveFreePlay && (
                <div>
                    <button className='button-user save-button-fixed button-color-freeplay' data-bs-toggle="modal"
                        data-bs-target="#addFrePlay" onClick={resetModalState} disabled={isPageLoading}>
                        New Free Plays
                    </button>
                </div>
            )}
            <div className="modal fade" id="addFrePlay" tabIndex={-1} ref={modalRef}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {isLoading
                                    ? 'Processing Transaction...'
                                    : isSuccess
                                        ? 'Success'
                                        : 'New Free Play Transaction'}
                            </h5>
                        </div>

                        <div className="modal-body">
                            <div className="centered-text">
                                {errorMessage && !isLoading && (
                                    <div className="alert alert-danger text-start">
                                        <strong>Error:</strong> {errorMessage}
                                    </div>
                                )}

                                {isLoading && (
                                    <>
                                        <span className="transactionManager2">Processing transaction, please wait...</span>
                                    </>
                                )}

                                {isSuccess && !isLoading && (
                                    <>
                                        <span className="transactionManager2">Transaction completed successfully!</span>
                                    </>
                                )}
                            </div>



                            {!isLoading && !isSuccess && !errorMessage && (
                                <div className="centered-text">
                                    <div className="mb-3">
                                        <span className="form-label transactionManager">Transaction</span>
                                        <select name="transactionType" className="selectManager" value={formTransaction.transactionType} onChange={handleInputChange}>
                                            <option value="deposit">Deposit</option>
                                            <option value="withdrawal">Withdrawal</option>
                                        </select>
                                    </div>
                                    <div className="mb-3 transaction-group-amount">
                                        <span className="transactionManager2">Amount</span>
                                        <input type="number" name="amount" className="numberTransaction" value={formTransaction.amount} autoComplete="off"
                                            onChange={handleInputChange} />
                                    </div>
                                    <div className="mb-3">
                                        <span className="transactionManager2">Description</span>
                                        <textarea name="description" className="descriptionManager" value={formTransaction.description}
                                            onChange={handleInputChange}
                                        ></textarea>
                                    </div>

                                </div>
                            )}
                        </div>

                        <div className="modal-footer d-flex justify-content-between">
                            {!isLoading && !isSuccess && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        data-bs-dismiss="modal"
                                    >
                                        Cancel
                                    </button>
                                    <button type="button" className="btn btn-success" onClick={saveTransaction}>Confirm</button>
                                </>
                            )}

                            {isSuccess && (
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    data-bs-dismiss="modal"
                                    onClick={resetModalState}
                                >
                                    Done
                                </button>
                            )}

                            {!isLoading && !isSuccess && errorMessage && (
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setErrorMessage(null)}
                                >
                                    Try again
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>


    )
}

export default UserFreePlay;